import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useDrop, useDrag, useDragLayer } from 'react-dnd'
import { DndProvider } from 'react-dnd'
import MouseBackEnd from 'react-dnd-mouse-backend'
import { Row, Col, Button } from '../../bootstrap/index.jsx'

import ActionButtons from './ActionButtons.jsx';
import { ACTION_TYPES } from './phaseReducer.js';
import {PHASE_MAPPING, NODE_SIZE, phaseElemPosition, PhaseElem, PhaseConnectorElem, PhaseConnectorCreateElem, DraggablePhaseElem} from './PhaseGraph.jsx';
import { MuiIcon } from "../../common/Icons.jsx";
import { Modal } from "../../common/Modal.jsx";
import WorkFlowStatusKey from './workflow_status_key.jsx';
import { PortalTrigger } from "../../common/Portal.jsx";

import {
  monitoring_report_update_phases_path,
  monitoring_report_project_reports_path
} from '~/routes';

// Scale between svg viewbox and screensize
const SVG_SCALE = 5;

// Draggable circle to create a new phase, can be dropped on a drop cell
const NewPhase = ({viewBoxWidth, viewBoxHeight, setDragging, type, setPhaseType}) => {
  const [{ isDragging }, drag, preview] = useDrag({
    item: { type: 'NewPhase' },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    })
  })

  useEffect(() => {
    setDragging(isDragging)
    setPhaseType(type)
  }, [isDragging])

  const pos = {
    x: 40 - NODE_SIZE*4, // Always keeping dots left-aligned
    // x: viewBoxWidth - NODE_SIZE*4,
    y: viewBoxHeight - 3
  }

  return <g ref={drag}>
    <circle 
      stroke='#DDD' 
      strokeWidth={1} 
      style={{opacity: isDragging ? 0.5 : 1}}
      className={''}
      fill={PHASE_MAPPING[type].color} 
      cx={pos.x} 
      cy={pos.y} 
      r={NODE_SIZE/4} 
    />
    <text 
      x={pos.x + NODE_SIZE/4 + 1}
      y={pos.y + 1}
      style={{fontSize: 2.5}}
      className='noselect'
    >{I18n.t('drag_to_add_new_phase', {name: I18n.t(type)})}</text>
  </g>
}

// Invisible cell used to detect drops
const DropCell = ({width, height, x,y, xLevel, yLevel, side, maxX, addCommentPhase, movePhase, mayDrop, phaseType, onHover}) => {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ['NewPhase', 'commenting', 'approval', 'docusign'],
    canDrop: (item, monitor) => {
      if (item.type == 'NewPhase') {
        return true // Always allowed, space created automatically
      } else { // Only allowed to empty space
        return mayDrop(xLevel, yLevel);
      }
    },
    drop: (item, monitor) => { 
      if (item.type == 'NewPhase') {
        addCommentPhase(xLevel, yLevel, side, phaseType)
      } else {
        movePhase(item.id, xLevel, yLevel, side)
      }
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    })
  })

  useEffect(() => {
    onHover(`${x}-${y}`, isOver);
  }, [isOver])

  if ((xLevel == 0 && side == 'left') || (xLevel == maxX && side == 'right')) {
    return null 
  }

  return <>
    {isOver && <circle 
      r={NODE_SIZE/4}       
      stroke='#DDD' 
      strokeWidth={1}
      fill={PHASE_MAPPING[phaseType].color}
      cx={x + width / 2} 
      cy={y + height / 2} 
    />}
    <rect 
      ref={drop}
      stroke="none"
      strokeWidth={0.1}
      fill="none" 
      width={width} 
      height={height} 
      x={x}  
      y={y}
    /> 
  </>
}

const CustomDragLayer = ({viewBoxWidth, viewBoxHeight, phaseType}) => {
  const { isDragging, currentOffset } =
    useDragLayer((monitor) => {
      return {
        currentOffset: monitor.getClientOffset(),
        isDragging: monitor.isDragging(),
      }
    })
  if (!isDragging || !currentOffset) {
    return null
  }

  const calculatePosition = (currentOffset) => {
    const containerPosition = $('.phase-selector-container').position();
    const containerWidth = $('.phase-selector-container').width();
    const containerHeight = $('.phase-selector-container').height();
  
    return {
      x: (currentOffset.x - containerPosition.left) / containerWidth * viewBoxWidth,
      y: (currentOffset.y - containerPosition.top + window.scrollY) / containerHeight * viewBoxHeight
    }
  }

  const pos = calculatePosition(currentOffset);

  return (
    <circle 
      stroke='#DDD' 
      strokeWidth={1} 
      style={{opacity: 1}}
      className={''}
      fill={PHASE_MAPPING[phaseType].color} 
      cx={pos.x} 
      cy={pos.y} 
      r={NODE_SIZE/4} 
    />
  )
}

// Invisible grid containing drop cells to detect drop positions
const DropGrid = ( {maxX, maxY, addCommentPhase, movePhase, mayDrop, phaseType, viewBoxWidth, viewBoxHeight}) => {
  const [hoveredCells, setHoveredCells] = useState({});
  // Each cell is split in to two horizontally (-1 for gap, so it's not possible)
  const cellWidth = NODE_SIZE  * 1.5 - 0.5;
  const cellHeight = NODE_SIZE * 1.5 - 0.5;  // (-0.5 for gap)
  const filledPos = []

  const handleHover = (key, isOver) => {
    setHoveredCells(prevCells => ({
      ...prevCells,
      [key]: isOver
    }));
  };

  const dragLayerVisible = useMemo(() => !Object.values(hoveredCells).includes(true), [hoveredCells])

  return <g>
    {dragLayerVisible && <CustomDragLayer viewBoxWidth={viewBoxWidth} viewBoxHeight={viewBoxHeight} phaseType={phaseType}/>}
    {[...Array((maxX + 1)).keys()].map(xIndex => (
      [...Array((maxY + 2)).keys()].map(yIndex => {
        const pos = phaseElemPosition(xIndex,yIndex,NODE_SIZE);
        // Avoid creating overlapping cells for fill & approve phase 
        if (filledPos.includes(pos.x + ',' + pos.y)){
          return null;
        }
        filledPos.push(pos.x + ',' + pos.y);

        return <g key={xIndex + '-' + yIndex} >
          <DropCell
            width={cellWidth} 
            height={cellHeight} 
            x={pos.x}  
            y={pos.y - (cellHeight / 2)}
            xLevel={xIndex}
            yLevel={yIndex}
            side='right'
            maxX={maxX}
            addCommentPhase={addCommentPhase}
            movePhase={movePhase}
            mayDrop={mayDrop}
            phaseType={phaseType}
            onHover={handleHover}
          />
          <DropCell
            width={cellWidth} 
            height={cellHeight} 
            x={pos.x - cellWidth - 0.5}  
            y={pos.y - (cellHeight / 2)}
            xLevel={xIndex}
            yLevel={yIndex}
            side='left'
            maxX={maxX}
            addCommentPhase={addCommentPhase}
            movePhase={movePhase}
            mayDrop={mayDrop}
            phaseType={phaseType}
            onHover={handleHover}
          />
        </g>
      })
    ))}
  </g>
}


const formatPhaseData = (phases) => {
  return phases.map(p => ({
      id: p.id,
      type: p.type,
      x_level: p.x_level,
      y_level: p.y_level,
      previous: p.previous,
  }))
}


export const HoverMenu = ({children, position, open, setOpen, ...rest}) => {
  const wrapperRef = useRef(null);

  const onOutsideClick = (e) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)){
      setOpen(false);
    }
  }

  useEffect(() => {
    window.addEventListener('click', onOutsideClick)
    return () => {window.removeEventListener('click', onOutsideClick)}
  }, [])

  return <div ref={wrapperRef} style={position && {position: 'absolute', left: position.x * SVG_SCALE, top: position.y * SVG_SCALE}} {...rest}>
    <Button size="xs" type="default" onClick={() => setOpen(true)}>
      <i className="fa fa-ellipsis-h"/>
    </Button>
    <div className='open' style={{position: 'relative', display: open ? "initial" : "none"}}>
      {children}
    </div>
  </div>
}


// Root component
const PhaseSelector = ({ phaseData, onSelect, selectedPhaseIdx, dispatch, reportId, showDocusign = false, options={} }) => {
  const svg = useRef(null);
  const connectorEndpoints = useRef([])
  const phases = phaseData.data;
  const editMode = phaseData.editMode;
  const [visibleMenuIdx, setVisibleMenuIdx] = useState(null);
  const [connectingNode, setConnectingNode] = useState(null);
  const [hoverMenuOpen, setHoverMenuOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [phaseType, setPhaseType] = useState("commenting")
  const maxY = Math.max(...phases.map(p => p.y_level));
  const maxX = Math.max(...phases.map(p => p.x_level));
  const viewBoxHeight = (maxY + 1) * NODE_SIZE * 1.5 + NODE_SIZE * 1.5 + 10;
  let viewBoxWidth = maxX * NODE_SIZE * 4 + NODE_SIZE;
  const WIDGET_SVG_SCALE = 8;

  // Fixes prompt text cutting off
  if (viewBoxWidth < 60) {
    viewBoxWidth = 60;
  }

  const onSave = () => {
    jQuery.post(monitoring_report_update_phases_path({id: reportId, _options: true}), {phases: formatPhaseData(phases)}, (res) => {
      dispatch({type:  ACTION_TYPES.DISABLE_EDIT_MODE});
      dispatch({type: ACTION_TYPES.UPDATE_DATA, payload: {data: res.data}})
    })
  }

  const closeMenu = () => {
    setVisibleMenuIdx(null);
    setHoverMenuOpen(false);
  }

  const onAddCommentPhase = (xLevel, yLevel, side, phaseType) => {
    if (phaseType == 'docusign'){
      dispatch({type:  ACTION_TYPES.ADD_DOCUSIGN_PHASE, payload: {xLevel: xLevel, yLevel: yLevel, side: side}});
    } else {
      dispatch({type:  ACTION_TYPES.ADD_COMMENT_PHASE, payload: {xLevel: xLevel, yLevel: yLevel, side: side}});
    }
  }

  const movePhase = (id, xLevel, yLevel, side) => { 
    closeMenu();
    dispatch({type:  ACTION_TYPES.MOVE_PHASE, payload: {id, xLevel: xLevel, yLevel: yLevel, side: side}});
  }

  const onEdit = () => dispatch({type:  ACTION_TYPES.ENABLE_EDIT_MODE})
  
  const onCancel = () => { 
    closeMenu();
    dispatch({type:  ACTION_TYPES.DISCARD_CHANGES}) 
  }
  
  const handlePhaseMouseEnter = (type, idx) => { !dragging && editMode && !hoverMenuOpen && setVisibleMenuIdx([type, idx]) }
  const handlePhaseMouseLeave = () => {}
  const onNewConnector = (idx) => { closeMenu(); setConnectingNode(idx) }
  const handleSelect = (idx) => { !connectingNode && onSelect(idx); }
  // const handleSelectWidget = (p) => { Turbo.visit(monitoring_report_project_reports_path({ id: p.monitoring_report_id, phase_id: p.id, _options: true }));}
  const handleNewConnection = (fromPhase, toPhase) => {
    // Logic for only allow 1 docusign after approval and if docusign is already after approval, then no node from or to it is allowed
    if (fromPhase.type === 'approval' && toPhase.type === 'docusign' && toPhase.previous.length === 0) {
      dispatch({type: ACTION_TYPES.ADD_NEW_CONNECTION, payload: {fromPhase: fromPhase, toPhase: toPhase}})
    } else if (fromPhase.type === 'docusign' && fromPhase.previous.length === 1 && toPhase.type !== 'docusign') {
      if (phases.find(phase => phase.id === fromPhase.previous[0]).type !== 'approval') {
          dispatch({type: ACTION_TYPES.ADD_NEW_CONNECTION, payload: {fromPhase: fromPhase, toPhase: toPhase}})
      }
    } else if (toPhase.type === 'docusign' && toPhase.previous.length === 1) {
      if (phases.find(phase => phase.id === toPhase.previous[0]).type !== 'approval') {
        dispatch({type: ACTION_TYPES.ADD_NEW_CONNECTION, payload: {fromPhase: fromPhase, toPhase: toPhase}})
      }
    } else {
      dispatch({type: ACTION_TYPES.ADD_NEW_CONNECTION, payload: {fromPhase: fromPhase, toPhase: toPhase}})
    }

    
    closeMenu();
    setConnectingNode(null);
  };

  const deletePhaseWithConfirm = (idx) => {
    closeMenu();
    if (confirm(I18n.t('delete_phase_confirm'))){
      dispatch({type: ACTION_TYPES.DELETE_COMMENT_PHASE, payload: {index: idx}});
    }
  }
  // Identifier in format phase_id + '-' + previous_phase_id
  const deleteConnection = (identifier) => {
    closeMenu();
    const toId = identifier.split('-')[0]
    const fromId = identifier.split('-')[1]
    dispatch({type: ACTION_TYPES.DELETE_CONNECTION, payload: {
      toPhaseId: isNaN(toId) ? toId : parseInt(toId, 10), 
      fromPhaseId: isNaN(fromId) ? fromId : parseInt(fromId, 10)
    }})
  }
  const nodePositions = phases.map(p => [p.x_level, p.y_level]);
  const connectorElemDetails = [];

  const mayDrop = (xLevel, yLevel) => {
    return !nodePositions.some((el) => el[0] == xLevel && el[1] == yLevel)
  }

  const connectorElems = phases
    .filter(p => p.hasOwnProperty('previous')).map(p => (
      p.previous.map((prevId, idx) => {
        const x1 = phases.find(p => p.id == prevId).x_level
        const y1 = phases.find(p => p.id == prevId).y_level
        const x2 = p.x_level
        const y2 = p.y_level
        const pos1 = phaseElemPosition(x1, y1, NODE_SIZE);
        const pos2 = phaseElemPosition(x2, y2, NODE_SIZE);
        connectorEndpoints.current.push([pos1, pos2]);
        const average = arr => arr.reduce( ( p, c ) => p + c, 0 ) / arr.length;
        connectorElemDetails.push([p.id + '-' + prevId, {x: average([pos1.x,pos2.x]), y: average([pos1.y, pos2.y])}])

        return <PhaseConnectorElem 
          key={'phase-connector-' + p.id + '-' + prevId }
          pos1={pos1}
          pos2={pos2}
          pos1Level={{x: x1, y:y1}}
          pos2Level={{x: x2, y:y2}}
          svgScale={options.isWidget ? WIDGET_SVG_SCALE : SVG_SCALE}
          strokeColor={ editMode && visibleMenuIdx != null && visibleMenuIdx[0] == 'connector' && p.id + '-' + prevId == visibleMenuIdx[1] ? '#666' : '#DDD'}
          onMouseEnter={() => handlePhaseMouseEnter('connector', p.id + '-' + prevId)}
          onMouseLeave={handlePhaseMouseLeave}  
          nodePositions={nodePositions}
          isWidget={options.isWidget}
        />
      })
    ));

  const KeyModal = ({ title, monitoringReportId }) => {
    return (
      <Modal title={title} size="xl" hideButtons>
        <WorkFlowStatusKey monitoringReportId={monitoringReportId}/>
      </Modal>
    );
  };

  const SubmissionsSummary = () => {
    return <div style={{display:'flex', justifyContent:'space-between'}}>
      {!editMode && !options.isWidget && <div style={{flexGrow: 1}}>
         <p className='description'>{I18n.t('click_phase_to_edit_details_info')}</p>
      </div>}
      {options.isWidget && <div className='workflow-summary-container' style={{flexGrow: 3}}>
        <div className='workflow-summary-content'>
          <div className='total-processed'>
            <p className='title'>{I18n.t('completed')}</p>
            <p className='number'>{options.workflowSummary.total_processed}</p>
          </div>
          <div className='total-description'>
            <p>
              <MuiIcon type="timelapse" className='icon-grey'/>
              <span>{options.workflowSummary.average_process_time} {I18n.t('unit_days')} </span>
              <span>{I18n.t('average_time_to_pass_workflow')}</span>
            </p>
            <p>
              <MuiIcon type="timelapse" className='icon-red'/>
              <span>{options.workflowSummary.longest_pass_time} {I18n.t('unit_days')} </span>
              <span>{I18n.t('longest_time_to_pass_workflow')}</span>
            </p>
          </div>
          <div className='view-details-button'>
            <PortalTrigger
              label={I18n.t("view_key")}
              btnElem="a"
              btnProps={{ type: "default", size: "xs", className: "ml-1" }}
              portalProps={{
                component: KeyModal,
                monitoringReportId: reportId
              }}
            />
          </div>
        </div>
      </div>}
    </div>
  }

  const Phase = editMode ? DraggablePhaseElem : PhaseElem;

  return <>
    {!options.isWidget && <h2 style={{marginTop: 0}}>{I18n.t('phases')}</h2>}
    <SubmissionsSummary/>
    <div style={{position: 'relative'}}>
    { 
      !editMode && !options.isWidget &&
        <Button 
          className="pull-right" 
          onClick={(e) => { e.preventDefault(); onEdit() }}
        >
          <i className="fa fa-pencil"/>
        </Button>
    }
    <svg
      className='phase-selector-container'
      ref={svg} 
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`} 
      style={options.isWidget ? {height: '100%', width: '100%'} : {height: viewBoxHeight * SVG_SCALE}}
    >
      <DndProvider backend={MouseBackEnd}>
        {connectorElems}
        {connectingNode != null && <PhaseConnectorCreateElem 
          svg={svg}
          fromPhase={phases[connectingNode]} 
          phases={phases}
          onConnect={handleNewConnection}
          svgScale={options.isWidget ? WIDGET_SVG_SCALE : SVG_SCALE}
          onEnd={() => setConnectingNode(null)}
        />}
        {editMode && <DropGrid 
          maxX={maxX} 
          maxY={maxY}
          addCommentPhase={onAddCommentPhase}
          movePhase={movePhase}
          mayDrop={mayDrop}
          phaseType={phaseType}
          viewBoxWidth={viewBoxWidth}
          viewBoxHeight={viewBoxHeight}
        />}

        {editMode && <NewPhase viewBoxHeight={viewBoxHeight- NODE_SIZE} viewBoxWidth={viewBoxWidth} setDragging={setDragging} type='commenting' setPhaseType={setPhaseType} />}
        {showDocusign && editMode && <NewPhase viewBoxHeight={viewBoxHeight} viewBoxWidth={viewBoxWidth} setDragging={setDragging} type='docusign' setPhaseType={setPhaseType} />}
        {phases.map((p,idx) => (
          <Phase 
            key={'phase-elem-' + idx}
            phase={p} 
            selected={!editMode && idx == selectedPhaseIdx }
            x={p.x_level} 
            y={p.y_level} 
            size={NODE_SIZE} 
            maxX={maxX}
            maxY={maxY}
            svg={svg}
            viewBoxWidth={viewBoxWidth}
            viewBoxHeight={viewBoxHeight}
            onMouseEnter={() => handlePhaseMouseEnter('node', idx)}
            onMouseLeave={handlePhaseMouseLeave}
            onClick={() => handleSelect(idx) }
            isWidget={options.isWidget}
            showDaysPassed={options.showDaysPassed}
          />
        ))}
      </DndProvider>
    </svg>
    {
      editMode && 
      !!visibleMenuIdx && 
      !(visibleMenuIdx[0] == 'node' && !['commenting','docusign', 'approval'].includes(phases[visibleMenuIdx[1]].type)) && 
      !dragging &&
        <HoverMenu
          position={ 
            visibleMenuIdx[0] == 'node' ? 
              phaseElemPosition(phases[visibleMenuIdx[1]].x_level, phases[visibleMenuIdx[1]].y_level, NODE_SIZE) :
              connectorElemDetails.find(c => c[0] == visibleMenuIdx[1])?.[1]}
          open={hoverMenuOpen}
          setOpen={setHoverMenuOpen}
        >
          <ul className="dropdown-menu">
            { visibleMenuIdx[0] == 'node' && 
              <>
                <li onClick={() => onNewConnector(visibleMenuIdx[1])}>
                  <a><i className="icon icon-edit text-info"></i>New connection</a>
                </li>
                {phases[visibleMenuIdx[1]].type != 'approval' &&
                <>
                  <li role="separator" className="divider"></li>
                  <li onClick={() => deletePhaseWithConfirm(visibleMenuIdx[1])}>
                    <a><i className="icon icon-trash text-danger"></i>Delete phase</a>
                  </li>
                </>}
              </>
            }
            { visibleMenuIdx[0] == 'connector' && 
              <>
                <li onClick={() => deleteConnection(visibleMenuIdx[1])}>
                  <a><i className="icon icon-trash text-danger"></i>Delete connection</a>
                </li>
              </>
            }
          </ul>
        </HoverMenu>
    }
    </div>
    {
      editMode && 
        <Row>
          <Col className="mb-1">
            <ActionButtons onSave={onSave} onCancel={onCancel} />
          </Col>
        </Row>    
    }

  </>
} 

export default PhaseSelector;
